/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the sae collapsible table component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */
import * as React from 'react';
import { faPen as faEdit } from '@fortawesome/pro-duotone-svg-icons/faPen';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChangeEvent, FunctionComponent, useCallback, useContext, useEffect, useState } from 'react';
import { InstitutionContext } from '@ngt/online-patient-management-ui';
import { useSnackbar } from 'notistack';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */
import client from '../../api/client';
import { useSaeById } from '../../store/modules/saereview/sae';
import * as Dtos from '../../api/Neon.Dtos'; 
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, Paper, TextField, Theme, Typography } from '@material-ui/core';


/*
 * ---------------------------------------------------------------------------------
 * interfaces
 * ---------------------------------------------------------------------------------
 */

interface ISaeNarrativeSummaryProps {
    patientStudyNumber: string,
    saeNumber: number,
    canAdministerSae: boolean
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    summaryContainer: {
        marginTop: theme.spacing(4),
        fontSize: '0.875rem',
    },
    summaryTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'end'
    },
    updateButton: {
        color: theme.palette.primary.main,
        padding: theme.spacing(0, 0.5),
        textTransform: 'none',
        fontWeight: 400,
        marginBottom: theme.spacing(-0.5),

        '&:hover': {
            cursor: 'pointer'
        },

        '& p': {
            fontSize: '0.875rem',
        }
    },
    icon: {
        marginRight: theme.spacing(0.5)
    },
    summary: {
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
        '& p': {
            fontSize: '0.875rem',
        }
    },
    pre: {
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
        margin: 0,
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        fontSize: '0.875rem',
        lineHeight: '1.43',
        letterSpacing: '0.01071em'
    },
    NoSummary: {
        opacity: '0.38',
        textAlign: 'center',
        fontSize: '0.875rem',
    },
    dialogTitle: {
        color: theme.palette.primary.main
    }
}));


/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */


/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const SaeNarrativeSummary: FunctionComponent<ISaeNarrativeSummaryProps> = ({
    patientStudyNumber,
    saeNumber,
    canAdministerSae
}) => {
    const classes = useStyles();

    const [sae, , actions] = useSaeById(patientStudyNumber, saeNumber);

    const [institution] = useContext(InstitutionContext);

    const [modalOpen, setmodalOpen] = useState(false);

    const toggleModal = useCallback(() => {
        setmodalOpen(state => !state);
    }, [setmodalOpen])

    const [value, setValue] = useState(sae?.medicalReview?.narrativeSummary);

    useEffect(() => {
        setValue(sae?.medicalReview?.narrativeSummary);
    }, [sae?.medicalReview?.narrativeSummary, setValue]);

    const [updating, setUpdating] = useState(false);

    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
    }, [setValue]);

    const { enqueueSnackbar } = useSnackbar();

    const onClick = useCallback(async () => {
        setUpdating(true);

        let success = false;

        try {
            await client.post(new Dtos.SaeMedicalReviewNarrativeSummaryPostSave({
                medicalReviewId: sae?.medicalReview?.id,
                narrativeSummary: value
            }))

            success = true;

            enqueueSnackbar('The narrative summary was updated successfully.', { variant: 'success' });
        }
        catch (error: any) {
            enqueueSnackbar('An error occurred while attempting to update the narrative summary.', { variant: 'success' });
        }
        finally {
            if (success) {
                actions.load();
                toggleModal();
            }

            setUpdating(false);
        }
    }, [setUpdating, actions, value, toggleModal, enqueueSnackbar, sae]);

    return (
        <>
            <Box className={classes.summaryContainer}>
                <Box className={classes.summaryTitle}>
                    <Typography
                        variant="h2"
                        color="primary"
                        style={{ marginRight: '2' }}
                    >
                        Narrative Summary
                    </Typography>

                    {
                        !!canAdministerSae && (
                            <Button className={classes.updateButton} onClick={toggleModal}>
                                <FontAwesomeIcon className={classes.icon} icon={faEdit} fixedWidth />
                                <Typography>
                                    Update
                                </Typography>
                            </Button>
                        )
                    }
                </Box>

                <Paper elevation={3} className={classes.summary}>
                    {
                        sae?.medicalReview?.narrativeSummary ?
                            <pre className={classes.pre}>
                                {sae.medicalReview.narrativeSummary}
                            </pre>
                            :
                            <Typography className={classes.noSummary}>
                                There is no narrative summary in the recent medical review.
                            </Typography>
                    }
                </Paper>

                <Dialog
                    open={modalOpen}
                    onClose={toggleModal}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle className={classes.dialogTitle} id="form-dialog-title">Update Narrative Summary</DialogTitle>
                    <DialogContent>
                        <TextField
                            variant="outlined"
                            multiline={true}
                            rows={8}
                            maxRows={25}
                            margin="dense"
                            id="name"
                            value={value}
                            onChange={handleChange}
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={toggleModal} color="secondary">
                            Cancel
                        </Button>
                        <Button onClick={onClick} color="primary" disabled={updating}>
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}
/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default SaeNarrativeSummary;
